import { Link } from "react-router-dom";
import {
  ArrowRight, ArrowUpRight, Heart, HandHelping, CalendarDays, Users, Megaphone, HandCoins,
} from "lucide-react";
import Hero from "@/components/Hero";
import Marquee from "@/components/Marquee";
import WhyCongoSection from "@/components/WhyCongoSection";
import Reveal from "@/components/Reveal";
import SectionHeading from "@/components/SectionHeading";
import DonateButton from "@/components/DonateButton";
import CareTimeline from "@/components/CareTimeline";
import StoriesSection from "@/components/StoriesSection";
import { HUMAN_CHALLENGES, WATER_ITEMS, IMG, WELLNESS_NOTE, WELLNESS_URL, INVOLVE_CARDS } from "@/lib/site";

const ICONS = { heart: Heart, hand: HandHelping, calendar: CalendarDays, users: Users, megaphone: Megaphone, coins: HandCoins };

export default function Home() {
  return (
    <>
      <Hero />
      <WhyCongoSection />
      <Marquee />

      {/* INTRODUCTION */}
      <section className="bg-ivory py-24 lg:py-32" data-testid="intro-section">
        <div className="mx-auto grid max-w-7xl gap-12 px-5 lg:grid-cols-12 lg:px-8">
          <div className="lg:col-span-7">
            <Reveal>
              <p className="eyebrow mb-6 text-earth">Introduction</p>
              <h2 className="text-balance font-display text-4xl font-semibold leading-[1.08] tracking-tight text-forest sm:text-5xl lg:text-6xl" data-testid="intro-headline">
                You don't have to live in Congo to <span className="italic text-earth">depend</span> on it.
              </h2>
            </Reveal>
          </div>
          <div className="flex flex-col justify-end lg:col-span-5">
            <Reveal delay={0.15}>
              <p className="text-lg leading-relaxed text-mutedink">
                From the minerals that power modern technology to the forests and freshwater systems that support our planet, Congo plays a profound role in the world around us.
              </p>
              <p className="mt-5 text-lg leading-relaxed text-mutedink">
                Yet many Congolese communities continue to face profound challenges in accessing safety, clean water, healthcare, economic opportunity and essential resources.
              </p>
              <p className="mt-5 text-lg leading-relaxed text-ink">
                Nseya Foundation exists to help bridge that gap — through practical projects, community support, awareness, culture and sustainable development.
              </p>
              <Link to="/programs" className="group mt-8 inline-flex items-center gap-2 font-mono text-xs font-semibold uppercase tracking-[0.25em] text-forest" data-testid="intro-why-congo-link">
                Why Congo? <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* THE HUMAN STORY */}
      <section className="relative overflow-hidden bg-forest py-24 text-ivory lg:py-32" data-testid="human-story-section">
        <div className="grain absolute inset-0" />
        <div className="relative mx-auto grid max-w-7xl items-center gap-14 px-5 lg:grid-cols-12 lg:px-8">
          <div className="lg:col-span-6">
            <SectionHeading
              dark
              eyebrow="The Human Story"
              title="The land is rich. The people deserve to thrive."
              sub="Communities in parts of the Democratic Republic of the Congo face profound challenges — often while living on land whose resources power the modern world."
            />
            <Reveal delay={0.1}>
              <ul className="mt-10 grid grid-cols-1 gap-3 sm:grid-cols-2" data-testid="human-challenges-list">
                {HUMAN_CHALLENGES.map((c) => (
                  <li key={c} className="flex items-start gap-3 border-b border-ivory/10 pb-3 text-sm leading-relaxed text-ivory/80">
                    <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-gold" aria-hidden="true" />
                    {c}
                  </li>
                ))}
              </ul>
              <p className="mt-8 max-w-xl text-sm leading-relaxed text-ivory/60">
                We name these realities with care — without sensationalizing suffering, and always centring the dignity of the people who live them.
              </p>
            </Reveal>
          </div>
          <div className="lg:col-span-6">
            <Reveal delay={0.15}>
              <div className="relative">
                <img src={IMG.minerDigging} alt="Miners working in a narrow hand-dug mining tunnel" className="aspect-[4/5] w-full rounded-2xl object-cover" loading="lazy" data-testid="human-story-image" />
                <div className="absolute -bottom-6 -left-6 hidden max-w-xs rounded-2xl bg-gold p-6 text-forest sm:block" data-testid="human-story-quote-card">
                  <p className="font-display text-lg italic leading-snug">
                    "The land is abundant. The people are overlooked. We choose to stand with them."
                  </p>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* CLEAN WATER */}
      <section id="water" className="scroll-mt-24 bg-ivory py-24 lg:py-32" data-testid="clean-water-section">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="grid items-center gap-14 lg:grid-cols-12">
            <div className="lg:col-span-6">
              <SectionHeading
                eyebrow="Clean Water"
                title="Water changes everything."
                sub="Before there can be sustainable development, there must be access to basic necessities. Clean water supports health, hygiene, food production, education and community life."
              />
              <Reveal delay={0.1}>
                <p className="mt-5 max-w-xl text-lg leading-relaxed text-mutedink">
                  Nseya Foundation is working toward bringing sustainable water access to communities — from the first well to lasting maintenance.
                </p>
                <ul className="mt-8 flex flex-wrap gap-2.5" data-testid="water-items-list">
                  {WATER_ITEMS.map((w) => (
                    <li key={w} className="rounded-full border border-forest/20 bg-white px-4 py-2 text-xs font-medium uppercase tracking-wider text-forest">
                      {w}
                    </li>
                  ))}
                </ul>
                <div className="mt-10">
                  <DonateButton testid="water-donate-button" />
                  <p className="mt-3 font-mono text-[11px] uppercase tracking-[0.2em] text-mutedink">Help build the foundation</p>
                </div>
              </Reveal>
            </div>
            <div className="lg:col-span-6">
              <div className="grid grid-cols-2 gap-5">
                <Reveal delay={0.1}>
                  <img src={IMG.borehole} alt="Children collecting clean water at a community borehole" className="aspect-[3/4] w-full rounded-2xl object-cover" loading="lazy" data-testid="water-image-1" />
                </Reveal>
                <Reveal delay={0.2} className="pt-10">
                  <img src={IMG.waterHands} alt="Hands receiving clean water" className="aspect-[3/4] w-full rounded-2xl object-cover" loading="lazy" data-testid="water-image-2" />
                </Reveal>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WOMEN & CHILDREN */}
      <section className="bg-sand py-24 lg:py-32" data-testid="women-children-section">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="grid items-center gap-14 lg:grid-cols-12">
            <div className="order-2 grid grid-cols-2 gap-5 lg:order-1 lg:col-span-6">
              <Reveal>
                <img src="/brand/river-market.jpg" alt="A riverside market on the Congo River - women and children gathering, selling and trading together" className="aspect-[3/4] w-full rounded-2xl object-cover" loading="lazy" data-testid="women-image-1" />
              </Reveal>
              <Reveal delay={0.15} className="pt-10">
                <img src={IMG.childrenGroup} alt="Children smiling together" className="aspect-[3/4] w-full rounded-2xl object-cover" loading="lazy" data-testid="women-image-2" />
              </Reveal>
            </div>
            <div className="order-1 lg:order-2 lg:col-span-6">
              <SectionHeading
                eyebrow="Women & Children"
                title="Protecting women. Protecting children. Protecting families."
                sub="In communities affected by conflict, displacement and exploitation, women and girls can face devastating forms of violence and abuse. Children can also be among those affected."
              />
              <Reveal delay={0.1}>
                <p className="mt-5 text-lg leading-relaxed text-mutedink">
                  The consequences can extend far beyond an individual survivor — affecting families, health, education, economic stability and the future of entire communities.
                </p>
                <p className="mt-5 text-lg leading-relaxed text-ink">
                  Nseya Foundation believes survivors deserve access to safety, compassionate care, dignity and pathways toward rebuilding their lives.
                </p>
                <Link to="/programs#sanctuary" data-testid="women-sanctuary-cta" className="group mt-9 inline-flex items-center gap-2 rounded-full bg-forest px-8 py-4 text-sm font-semibold uppercase tracking-[0.18em] text-ivory transition-all duration-300 hover:bg-earthy hover:bg-earth">
                  Our Sanctuary Vision <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </Link>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* SANCTUARY */}
      <section className="relative overflow-hidden bg-charcoal py-24 text-ivory lg:py-32" data-testid="sanctuary-section">
        <div className="grain absolute inset-0" />
        <div className="relative mx-auto max-w-7xl px-5 lg:px-8">
          <SectionHeading
            dark
            center
            eyebrow="Conceptual Master Plan — Long-Term Vision"
            title="The Nseya Women & Children's Sanctuary"
            sub="A safe place to heal, learn, rebuild and thrive. The sanctuary is envisioned as more than a shelter — a community designed around medical care, safety, education, healing, agriculture, vocational training, water, energy and economic opportunity."
          />
          <Reveal delay={0.1} className="mt-14">
            <Link to="/programs#sanctuary" className="group relative block overflow-hidden rounded-2xl border border-ivory/15" data-testid="home-masterplan-link">
              <img src={IMG.masterplan} alt="Nseya Women & Children's Sanctuary conceptual master plan" className="w-full" loading="lazy" data-testid="home-masterplan-image" />
              <div className="absolute inset-0 flex items-end justify-center bg-gradient-to-t from-charcoal/80 via-transparent to-transparent pb-8">
                <span className="inline-flex items-center gap-2 rounded-full bg-gold px-7 py-3.5 text-sm font-semibold uppercase tracking-[0.18em] text-forest transition-transform duration-300 group-hover:scale-105">
                  Explore the Master Plan <ArrowUpRight className="h-4 w-4" />
                </span>
              </div>
            </Link>
          </Reveal>

          <div className="mt-24 grid gap-14 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <SectionHeading
                dark
                eyebrow="Sanctuary Philosophy"
                title="Care comes first."
                sub="For women and children who have experienced violence, access to compassionate medical and psychosocial care can be an essential first step toward recovery. Our sanctuary vision therefore begins with a medical and wellness center — followed by safe housing, education, nutrition, skills development, agriculture and sustainable livelihoods."
              />
              <Reveal delay={0.1}>
                <Link to="/about" data-testid="sanctuary-philosophy-link" className="mt-8 inline-flex items-center gap-2 font-mono text-xs font-semibold uppercase tracking-[0.25em] text-gold">
                  Our philosophy <ArrowRight className="h-4 w-4" />
                </Link>
              </Reveal>
            </div>
            <div className="lg:col-span-7">
              <CareTimeline />
            </div>
          </div>
        </div>
      </section>

      {/* WELLNESS */}
      <section id="wellness" className="scroll-mt-24 bg-ivory py-24 lg:py-32" data-testid="wellness-section">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="grid items-center gap-14 lg:grid-cols-12">
            <div className="lg:col-span-6">
              <SectionHeading
                eyebrow="Wellness"
                title="What if wellness could create change?"
                sub="Nseya wellness workshops and retreats create spaces for people to slow down, reconnect, move, breathe, learn and restore. But the experience doesn't end with the individual — participation helps raise funds for Nseya Foundation's humanitarian work and long-term sanctuary vision."
              />
              <Reveal delay={0.1}>
                <p className="mt-5 font-display text-2xl italic text-forest" data-testid="wellness-tagline">
                  You come to heal. You leave having helped build something.
                </p>
                <div className="mt-6 flex flex-wrap items-center gap-4">
                  <a href={WELLNESS_URL} target="_blank" rel="noopener noreferrer" data-testid="wellness-retreats-cta" className="inline-flex items-center gap-2 rounded-full border border-forest/40 px-8 py-4 text-sm font-semibold uppercase tracking-[0.18em] text-forest transition-all duration-300 hover:bg-forest hover:text-ivory">
                    Explore Wellness Retreats <ArrowUpRight className="h-4 w-4" />
                  </a>
                </div>
                <div className="mt-8 rounded-2xl border-l-4 border-gold bg-sand p-6" data-testid="wellness-proceeds-note">
                  <p className="text-sm leading-relaxed text-ink">
                    <strong className="font-semibold">50% of proceeds</strong> from the Nseya Wellness Retreat go directly to the Nseya Humanitarian Foundation. When you invest in your wellness, you invest in others. Learn more at{" "}
                    <a href={WELLNESS_URL} target="_blank" rel="noopener noreferrer" className="font-semibold underline decoration-gold underline-offset-4">www.nseyawellness.com</a>
                  </p>
                </div>
              </Reveal>
            </div>
            <div className="lg:col-span-6">
              <div className="grid grid-cols-2 gap-5">
                <Reveal>
                  <img src="/brand/sunrise-silhouette.jpg" alt="A woman stretching toward the rising sun by the ocean" data-testid="wellness-image-1" className="aspect-[3/4] w-full rounded-2xl object-cover" loading="lazy" />
                </Reveal>
                <Reveal delay={0.15} className="pt-10">
                  <img src={IMG.wellnessGroup} alt="A group wellness session outdoors" className="aspect-[3/4] w-full rounded-2xl object-cover" loading="lazy" />
                </Reveal>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FEED TWO BIRDS */}
      <section className="relative overflow-hidden bg-sand py-24 lg:py-32" data-testid="feed-two-birds-section">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="grid items-center gap-14 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <Reveal>
                <img src={IMG.wellnessDock} alt="A person resting in meditation at sunrise" className="aspect-[4/5] w-full rounded-2xl object-cover" loading="lazy" data-testid="feed-two-birds-image" />
              </Reveal>
            </div>
            <div className="lg:col-span-7">
              <SectionHeading
                eyebrow="The Nseya Philosophy"
                title={<span className="italic">Feed two birds with one hand.</span>}
              />
              <Reveal delay={0.1}>
                <div className="mt-6 space-y-5 text-lg leading-relaxed text-mutedink">
                  <p>We often think we have to choose between caring for ourselves and caring for others. We don't.</p>
                  <p>When we nourish ourselves, we can create more capacity to nourish our communities. When we reconnect with nature, we become more conscious of the world we share. When we gather through wellness, culture and community, our participation can help create opportunities for people far beyond us.</p>
                  <p className="text-ink">One experience can create a ripple far beyond the person who attends it.</p>
                </div>
              </Reveal>
              <Reveal delay={0.15}>
                <div className="mt-10 flex flex-col items-start gap-3 sm:flex-row sm:items-center" data-testid="feed-two-birds-flow">
                  {["Your Wellness", "Community Support", "Change for Congo"].map((step, i) => (
                    <div key={step} className="flex items-center gap-3">
                      <span className={`rounded-full px-5 py-2.5 font-mono text-[11px] font-semibold uppercase tracking-[0.18em] ${i === 2 ? "bg-gold text-forest" : "border border-forest/30 text-forest"}`}>
                        {step}
                      </span>
                      {i < 2 && <ArrowRight className="h-4 w-4 text-ochre" />}
                    </div>
                  ))}
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* DANCE FOR CONGO */}
      <section className="relative overflow-hidden bg-earth py-28 text-ivory lg:py-36" data-testid="dance-section">
        <img src={IMG.danceTraditional} alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover opacity-25" loading="lazy" />
        <div className="absolute inset-0 bg-gradient-to-r from-earth via-earth/85 to-earth/40" />
        <div className="relative mx-auto max-w-7xl px-5 lg:px-8">
          <SectionHeading dark eyebrow="Culture" title="Dance for Congo" sub="Culture is connection." />
          <Reveal delay={0.1}>
            <p className="mt-6 max-w-2xl text-lg leading-relaxed text-ivory/85">
              Dance is one of the ways Congo has contributed to the cultural world. Congolese and broader Central African movement traditions have had a significant influence on African and global popular culture.
            </p>
            <p className="mt-5 max-w-2xl text-lg leading-relaxed text-ivory/85">
              Dance for Congo is a cultural experience that invites people to explore movement, music, rhythm and the diverse traditions connected to Congo — combining dance, music, food, storytelling, community and fundraising.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/get-involved#dance" data-testid="dance-join-cta" className="inline-flex items-center gap-2 rounded-full bg-gold px-8 py-4 text-sm font-semibold uppercase tracking-[0.18em] text-forest transition-all duration-300 hover:bg-golddark">
                Join the Next Event <ArrowRight className="h-4 w-4" />
              </Link>
              <DonateButton variant="outlineLight" testid="dance-donate-button" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* FOUNDER TEASER */}
      <section className="relative overflow-hidden bg-ivory py-24 lg:py-32" data-testid="founder-teaser-section">
        <div className="mx-auto grid max-w-7xl items-center gap-14 px-5 lg:grid-cols-12 lg:px-8">
          <div className="lg:col-span-5">
            <Reveal>
              <img src="/brand/founder-portrait.jpg" alt="Nseya Ariane Kamba, founder of the Nseya Foundation" className="aspect-[4/5] w-full rounded-2xl object-cover" loading="lazy" data-testid="founder-teaser-image" />
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            <SectionHeading eyebrow="Founder" title="The road always leads back home." />
            <Reveal delay={0.1}>
              <p className="mt-6 max-w-2xl font-display text-xl italic leading-relaxed text-ink sm:text-2xl" data-testid="founder-teaser-quote">
                "Congo is part of my history, my heartbeat and my responsibility. I created Nseya Foundation to return to that source — to support people whose lives and communities deserve to be seen, heard and supported."
              </p>
              <p className="mt-6 font-mono text-xs uppercase tracking-[0.25em] text-ochre" data-testid="founder-name">
                Nseya Ariane Kamba — Founder
              </p>
              <Link to="/about" data-testid="founder-read-story-cta" className="group mt-8 inline-flex items-center gap-2 rounded-full border border-forest/40 px-8 py-4 text-sm font-semibold uppercase tracking-[0.18em] text-forest transition-all duration-300 hover:bg-forest hover:text-ivory">
                Read Her Story <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      <StoriesSection />

      {/* GET INVOLVED STRIP */}
      <section className="bg-sand py-24 lg:py-32" data-testid="get-involved-strip-section">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <SectionHeading
            center
            eyebrow="Get Involved"
            title="There are many ways to create change."
          />
          <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="get-involved-strip-grid">
            {INVOLVE_CARDS.map((card, i) => {
              const Icon = ICONS[card.icon];
              return (
                <Reveal key={card.title} delay={i * 0.06}>
                  <div className="flex h-full items-start gap-5 rounded-2xl border border-hairline bg-white p-7 transition-shadow duration-300 hover:shadow-lg" data-testid={`get-involved-strip-card-${i + 1}`}>
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-forest text-gold">
                      <Icon className="h-5 w-5" />
                    </span>
                    <div>
                      <h3 className="font-display text-xl font-semibold text-forest">{card.title}</h3>
                      <p className="mt-1.5 text-sm leading-relaxed text-mutedink">{card.desc}</p>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
          <Reveal className="mt-12 text-center">
            <Link to="/get-involved" data-testid="get-involved-strip-cta" className="inline-flex items-center gap-2 rounded-full bg-forest px-8 py-4 text-sm font-semibold uppercase tracking-[0.18em] text-ivory transition-all duration-300 hover:bg-earth">
              Get Involved <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="relative overflow-hidden bg-forest py-28 text-center text-ivory lg:py-36" data-testid="final-cta-section">
        <div className="grain absolute inset-0" />
        <div className="relative mx-auto max-w-4xl px-5 lg:px-8">
          <Reveal>
            <h2 className="text-balance font-display text-4xl font-semibold leading-[1.1] tracking-tight sm:text-5xl lg:text-6xl" data-testid="final-cta-headline">
              You don't have to live in Congo to depend on it.
            </h2>
            <p className="mt-6 font-display text-2xl italic text-gold" data-testid="final-cta-you-already-do">You already do.</p>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="mx-auto mt-8 max-w-2xl space-y-4 text-lg leading-relaxed text-ivory/80">
              <p>Through the technology we use. Through the resources that power modern life. Through the forests, water and ecosystems that connect us all. And through the human stories that deserve to be seen.</p>
              <p className="text-ivory">Change begins when we choose to participate.</p>
            </div>
            <div className="mt-12">
              <DonateButton variant="big" testid="final-donate-button" />
            </div>
            <p className="mt-14 font-mono text-[11px] uppercase tracking-[0.3em] text-ivory/60" data-testid="final-cta-brand">
              Nseya Foundation · Change for Congo
            </p>
            <p className="mt-2 font-display text-sm italic text-ivory/70">Safety. Dignity. Clean Water. Community.</p>
          </Reveal>
        </div>
      </section>
    </>
  );
}
